/**
 * offlineGetCache.js — IndexedDB-backed response cache for GET/read API calls.
 *
 * Stores the last successful JSON response per cache key so the app can
 * serve stale data when the device is offline. Transparent layer — callers
 * don't need to know whether the response came from cache or network.
 *
 * Cache key = url + sorted params/body hash (for POST-reads like get-the-template).
 */

const DB_NAME = "VestaOfflineGetCache";
const DB_VERSION = 1;
const STORE_NAME = "responses";

/** Cached responses older than this are considered stale and ignored offline. */
const CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const isFresh = (entry) => entry && (Date.now() - (entry.ts ?? 0)) < CACHE_TTL_MS;

/* ── helpers ─────────────────────────────────────────────────────────── */

let _db = null;

const openDB = () =>
    new Promise((resolve, reject) => {
        if (_db) return resolve(_db);
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = (e) => {
            const db = e.target.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: "key" });
            }
        };
        req.onsuccess = (e) => {
            _db = e.target.result;
            resolve(_db);
        };
        req.onerror = (e) => reject(e.target.error);
    });

let _idbOk = null;
const isIDBAvailable = async () => {
    if (_idbOk !== null) return _idbOk;
    try {
        await openDB();
        _idbOk = true;
    } catch {
        _idbOk = false;
    }
    return _idbOk;
};

/* localStorage fallback */
const LS_KEY = "_vesta_get_cache";

const readLS = () => {
    try {
        return JSON.parse(localStorage.getItem(LS_KEY) || "{}");
    } catch {
        return {};
    }
};

const writeLS = (obj) => {
    try {
        localStorage.setItem(LS_KEY, JSON.stringify(obj));
    } catch {
        /* quota exceeded — skip silently */
    }
};

/* ── cache key builder ───────────────────────────────────────────────── */

/**
 * Build a deterministic cache key from url + optional body/params.
 * Keeps it short and collision-resistant.
 */
export const buildCacheKey = (url, paramsOrBody) => {
    let suffix = "";
    if (paramsOrBody && typeof paramsOrBody === "object") {
        try {
            const sorted = Object.keys(paramsOrBody)
                .sort()
                .reduce((o, k) => {
                    o[k] = paramsOrBody[k];
                    return o;
                }, {});
            suffix = "|" + JSON.stringify(sorted);
        } catch {
            /* ignore */
        }
    }
    return url + suffix;
};

/* ── public API ──────────────────────────────────────────────────────── */

/**
 * Store a response in cache.
 * @param {string} key   — cache key (from buildCacheKey)
 * @param {*}      data  — response.data (JSON-serialisable)
 */
export const put = async (key, data) => {
    const entry = { key, data, ts: Date.now() };

    if (await isIDBAvailable()) {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, "readwrite");
            tx.objectStore(STORE_NAME).put(entry);
            tx.oncomplete = () => resolve();
            tx.onerror = (e) => reject(e.target.error);
        });
    }

    const store = readLS();
    store[key] = entry;
    writeLS(store);
};

/**
 * Retrieve cached response data.
 * @param {string} key
 * @returns {Promise<*|null>} — cached data or null if miss
 */
export const get = async (key) => {
    if (await isIDBAvailable()) {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, "readonly");
            const req = tx.objectStore(STORE_NAME).get(key);
            req.onsuccess = () => {
                const entry = req.result;
                resolve(isFresh(entry) ? entry.data : null);
            };
            req.onerror = (e) => reject(e.target.error);
        });
    }

    const store = readLS();
    const entry = store[key];
    return isFresh(entry) ? entry.data : null;
};

/**
 * Remove a specific cache entry.
 */
export const remove = async (key) => {
    if (await isIDBAvailable()) {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, "readwrite");
            tx.objectStore(STORE_NAME).delete(key);
            tx.oncomplete = () => resolve();
            tx.onerror = (e) => reject(e.target.error);
        });
    }

    const store = readLS();
    delete store[key];
    writeLS(store);
};
